<template>
  <div class="receipt-58" :style="{ width: widthClass === 'thermal' ? '58mm' : undefined }">
    <div class="rc-header">
      <div v-if="tenant.logo_url" class="rc-logo"><img :src="tenant.logo_url" alt="" /></div>
      <div class="rc-biz">{{ tenant.name }}</div>
      <div v-if="shop && shop.name" class="rc-shop">{{ shop.name }}</div>
      <div v-if="tenant.city" class="rc-line muted">{{ tenant.city }}</div>
      <div v-if="tenant.phone" class="rc-line muted">{{ tenant.phone }}</div>
      <div v-if="tenant.receipt_header" class="rc-header-msg">{{ tenant.receipt_header }}</div>
    </div>

    <div class="rc-divider"></div>

    <div class="rc-meta">
      <div><span>Receipt:</span> <strong>{{ sale.receipt_no }}</strong></div>
      <div><span>Date:</span> {{ sale.created_at }}</div>
      <div v-if="cashier"><span>Cashier:</span> {{ cashier.name }}</div>
      <div v-if="customer"><span>Customer:</span> {{ customer.name }}<span v-if="customer.phone"> · {{ customer.phone }}</span></div>
      <div><span>Status:</span> {{ sale.status }}</div>
    </div>

    <div class="rc-divider"></div>

    <div class="rc-items">
      <div v-for="it in items" :key="it.id" class="rc-item">
        <div class="rc-item-name">
          {{ it.variant_name_snapshot }}
          <span v-if="it.is_returned || it.returned_qty > 0" class="rc-returned"> [returned {{ Math.round(it.returned_qty) }}]</span>
        </div>
        <div class="rc-item-row">
          <span>{{ formatQty(it.quantity) }} × {{ format(it.unit_price) }}</span>
          <span class="rc-item-total">{{ format(it.line_total) }}</span>
        </div>
        <div v-if="it.discount_amount > 0" class="rc-item-discount">discount −{{ format(it.discount_amount) }}</div>
      </div>
    </div>

    <div class="rc-divider"></div>

    <div class="rc-totals">
      <div class="rc-line"><span>Subtotal</span><span>{{ format(sale.subtotal) }}</span></div>
      <div v-if="sale.discount_amount > 0" class="rc-line"><span>Discount</span><span>−{{ format(sale.discount_amount) }}</span></div>
      <div v-if="sale.tax_amount > 0" class="rc-line"><span>Tax</span><span>{{ format(sale.tax_amount) }}</span></div>
      <div class="rc-line rc-grand"><span>TOTAL</span><span>KES {{ format(sale.total) }}</span></div>
    </div>

    <div class="rc-divider"></div>

    <div class="rc-pay">
      <div v-for="(p, i) in payments" :key="i" class="rc-line">
        <span>{{ prettyMethod(p.method) }}<span v-if="p.mpesa_receipt || p.reference" class="rc-ref"> {{ p.mpesa_receipt || p.reference }}</span></span>
        <span :class="{ 'rc-neg': p.amount < 0 }">{{ format(p.amount) }}</span>
      </div>
    </div>

    <div class="rc-footer">
      <div v-if="tenant.receipt_footer" class="rc-footer-msg">{{ tenant.receipt_footer }}</div>
      <div v-if="tenant.receipt_show_customer_link" class="rc-link">Digital receipt: {{ publicUrl }}</div>
      <div v-if="tenant.receipt_show_qr" class="rc-qr">
        <!-- SVG QR via Google Charts fallback; renderer is client-side text-based -->
        <img :src="qrSrc" alt="QR" width="120" height="120" />
      </div>
      <div class="rc-brand muted">Powered by Paysol · paysol.africa</div>
    </div>
  </div>
</template>

<script>
const METHOD_LABEL = {
  cash: 'Cash', mpesa_stk: 'M-Pesa STK', mpesa_till: 'M-Pesa',
  card: 'Card', wallet: 'Wallet', store_credit: 'Store credit', bank_transfer: 'Bank',
};

export default {
  name: 'PrintableReceipt',
  props: {
    sale: { type: Object, required: true },
    tenant: { type: Object, required: true },
    shop: { type: Object, default: null },
    cashier: { type: Object, default: null },
    customer: { type: Object, default: null },
    items: { type: Array, default: () => [] },
    payments: { type: Array, default: () => [] },
    widthClass: { type: String, default: 'thermal' },   // 'thermal' | 'digital'
  },
  computed: {
    publicUrl() {
      const origin = typeof window !== 'undefined' ? window.location.origin : 'https://www.paysol.africa';
      return `${origin}/#/r/${this.sale.receipt_no}`;
    },
    qrSrc() {
      // Uses public QR image API — cheap, no dep. In prod, swap for a local QR lib.
      const url = encodeURIComponent(this.publicUrl);
      return `https://quickchart.io/qr?text=${url}&size=200&margin=1`;
    },
  },
  methods: {
    format(n) { return Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
    formatQty(n) { const x = Number(n || 0); return x % 1 === 0 ? String(x) : x.toFixed(3); },
    prettyMethod(m) { return METHOD_LABEL[m] || m; },
  },
};
</script>

<style scoped>
.receipt-58 {
  font-family: 'JetBrains Mono', 'Courier New', monospace;
  font-size: 11px; line-height: 1.35; color: #000; background: #fff;
  padding: 6mm 4mm;
  max-width: 100%;
}
.rc-header { text-align: center; }
.rc-logo img { max-height: 40px; margin-bottom: 4px; }
.rc-biz { font-weight: 700; font-size: 14px; }
.rc-shop { font-weight: 600; font-size: 12px; }
.rc-line { display: flex; justify-content: space-between; gap: 6px; }
.rc-header-msg { font-style: italic; margin-top: 4px; }
.rc-divider { border-top: 1px dashed #000; margin: 6px 0; }
.rc-meta > div { display: flex; justify-content: space-between; gap: 8px; }
.rc-meta span:first-child { color: #555; }
.rc-item { margin-bottom: 4px; }
.rc-item-name { font-weight: 600; }
.rc-item-row { display: flex; justify-content: space-between; }
.rc-item-total { font-weight: 600; }
.rc-item-discount { font-size: 10px; color: #555; text-align: right; }
.rc-returned { color: #b00; font-size: 10px; }
.rc-totals .rc-grand { font-weight: 700; font-size: 13px; margin-top: 4px; padding-top: 4px; border-top: 1px solid #000; }
.rc-ref { color: #555; font-size: 10px; margin-left: 4px; }
.rc-neg { color: #b00; }
.rc-footer { text-align: center; margin-top: 6px; }
.rc-footer-msg { font-style: italic; margin-bottom: 4px; }
.rc-link { font-size: 9px; word-break: break-all; color: #333; margin-bottom: 4px; }
.rc-qr { display: flex; justify-content: center; margin: 6px 0; }
.rc-qr img { max-width: 32mm; }
.rc-brand { font-size: 9px; margin-top: 6px; }
.muted { color: #666; }

/* Print — target thermal roll */
@media print {
  @page { size: 58mm auto; margin: 0; }
  html, body { margin: 0; padding: 0; background: #fff !important; color: #000 !important; }
  .receipt-58 { width: 58mm !important; padding: 3mm; }
}
</style>
